import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, VENDAS, OPERACIONAL, EMPRESAS, SO_MASTER, PROPOSTAS_LEITURA, DASHBOARD } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Building2,
  Users,
  LogOut,
  User,
  Link2,
  ClipboardList,
  FileText,
  AlertTriangle,
  Wallet,
  PanelLeftClose,
  PanelLeftOpen,
  Menu,
  Calculator,
} from 'lucide-react';
import { lazy, Suspense, useState, useEffect, useRef } from 'react';
import UserProfileModal from '@/components/UserProfileModal';
import LimitePet from '@/components/pet/LimitePet';
import { usePet } from '@/components/pet/usePet';

// O three.js só baixa para quem tem o pet na tela.
const PetWidget = lazy(() => import('@/components/pet/PetWidget'));

// Casca do sistema E "Cobalto" (docs/DESIGN.md): menu lateral marfim com o
// logotipo original e sem barra no topo. O título de cada tela vive na capa
// cobalto da própria página; usuário e tema ficam no pé do menu.
const Layout = () => {
  const { user, logout } = useAuth();
  const { preference, setPreference } = useTheme();
  const pet = usePet(user);
  // com o pet na tela, o fim da página ganha folga para o círculo não cobrir o último botão
  const petVisivel = Boolean(pet.estado?.existe && pet.estado.visivel);
  const location = useLocation();
  const navigate = useNavigate();
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Abaixo de lg a barra vira gaveta (menuMobile) e sempre mostra os nomes.
  const [menuMobile, setMenuMobile] = useState(false);
  const expandido = sidebarOpen || menuMobile;

  useEffect(() => { setMenuMobile(false); }, [location.pathname]);
  const asideRef = useRef(null);
  const botaoMenuRef = useRef(null);
  // Gaveta aberta: foco vai para o menu e fica preso nele, Esc fecha e devolve o foco, a página não rola por baixo.
  useEffect(() => {
    if (!menuMobile) return undefined;
    asideRef.current?.querySelector('a')?.focus();
    const overflowAntes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const teclado = (e) => {
      if (e.key === 'Escape') {
        setMenuMobile(false);
        botaoMenuRef.current?.focus();
        return;
      }
      if (e.key !== 'Tab' || !asideRef.current) return;
      const focaveis = [...asideRef.current.querySelectorAll('a[href], button:not([disabled])')].filter((el) => el.offsetParent !== null);
      if (!focaveis.length) return;
      const primeiro = focaveis[0];
      const ultimo = focaveis[focaveis.length - 1];
      if (e.shiftKey && document.activeElement === primeiro) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primeiro.focus(); }
    };
    window.addEventListener('keydown', teclado);
    return () => {
      document.body.style.overflow = overflowAntes;
      window.removeEventListener('keydown', teclado);
    };
  }, [menuMobile]);

  // Navegação agrupada por contexto (grupo sem `label` fica sem cabeçalho —
  // usado só pro Dashboard, que é o item "raiz"). Cada item restrito usa os
  // mesmos perfis da sua rota (App.js); grupo sem item visível some.
  const pode = (perfis) => perfis.includes(user?.role);
  const navigationGroups = [
    { items: [pode(DASHBOARD) && { name: 'Dashboard', href: '/', icon: LayoutDashboard }] },
    { label: 'Catálogo', items: [{ name: 'Produtos', href: '/produtos', icon: Package }] },
    {
      label: 'Vendas',
      items: [
        pode(PROPOSTAS_LEITURA) && { name: 'Propostas', href: '/propostas', icon: FileText },
        pode(VENDAS) && { name: 'Pedidos', href: '/pedidos', icon: ShoppingCart },
        pode(VENDAS) && { name: 'Simulador Cash Black', href: '/simulador-cash-black', icon: Calculator },
      ],
    },
    {
      label: 'Operacional',
      items: pode(OPERACIONAL) ? [
        { name: 'Financeiro', href: '/financeiro', icon: Wallet },
        { name: 'Gestão de pedidos', href: '/gestao-pedidos', icon: ClipboardList },
        { name: 'Erros Tiny', href: '/pedidos-erro-tiny', icon: AlertTriangle },
      ] : [],
    },
    {
      label: 'Ferramentas',
      items: [
        { name: 'Links BCMED', href: '/links-bcmed', icon: Link2 },
        pode(EMPRESAS) && { name: 'Empresas', href: '/empresas', icon: Building2 },
        pode(SO_MASTER) && { name: 'Usuários', href: '/usuarios', icon: Users },
      ],
    },
  ]
    .map((g) => ({ ...g, items: g.items.filter(Boolean) }))
    .filter((g) => g.items.length > 0);

  const navigation = navigationGroups.flatMap((g) => g.items);

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    // por segmento: /pedidos casa /pedidos/novo, mas não /pedidos-erro-tiny
    if (path !== '/' && (location.pathname === path || location.pathname.startsWith(`${path}/`))) return true;
    return false;
  };

  // Nome da página atual — usado no título da aba do navegador.
  const currentPage = navigation.find((item) => isActive(item.href));
  const pageTitle = currentPage?.name || 'Dashboard';

  useEffect(() => {
    document.title = `${pageTitle} · BCMED`;
  }, [pageTitle]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return (name || '')
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const navLink = (item) => {
    const Icon = item.icon;
    const active = isActive(item.href);
    const link = (
      <Link
        to={item.href}
        aria-current={active ? 'page' : undefined}
        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
        className={`flex items-center gap-2.5 rounded-lg py-2 text-[13.5px] transition-colors duration-150 ${expandido ? 'px-3' : 'justify-center px-0'} ${
          active
            ? 'bg-blue-50 font-semibold text-blue-600 dark:bg-blue-500/15 dark:text-blue-300'
            : 'font-medium text-gray-600 hover:bg-accent hover:text-foreground dark:text-gray-400'
        }`}
      >
        <Icon size={16} strokeWidth={1.75} className="shrink-0" />
        {expandido && <span className="truncate">{item.name}</span>}
      </Link>
    );
    if (expandido) return <div key={item.name}>{link}</div>;
    return (
      <Tooltip key={item.name}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{item.name}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex min-h-screen bg-background">
        {menuMobile && (
          <div aria-hidden className="fixed inset-0 z-30 bg-gray-950/50 lg:hidden" onClick={() => setMenuMobile(false)} />
        )}
        <aside
          id="menu-lateral"
          ref={asideRef}
          {...(menuMobile ? { role: 'dialog', 'aria-modal': true, 'aria-label': 'Menu' } : {})}
          className={`fixed inset-y-0 left-0 z-40 flex flex-col border-r border-border bg-background duration-200 ${expandido ? 'w-56' : 'w-[72px]'} ${menuMobile
            // abrir: aparece na hora (o foco já entra); fechar: some só depois de deslizar
            ? 'translate-x-0 shadow-pop transition-[width,transform]'
            : '-translate-x-full max-lg:invisible lg:translate-x-0 transition-[width,transform,visibility]'}`}
        >
          <div className={`flex h-16 shrink-0 items-center ${expandido ? 'px-5' : 'justify-center'}`}>
            <Link to="/" aria-label="BCMED, ir para o Dashboard">
              {expandido ? (
                <img src="/logo-bcmed.png" alt="BCMED" className="h-9 w-auto dark:brightness-0 dark:invert" />
              ) : (
                // só o "B" da marca: o PNG tem o símbolo nos primeiros ~29% da largura
                <span className="block h-10 w-6 overflow-hidden">
                  <img src="/logo-bcmed.png" alt="BCMED" className="h-10 w-auto max-w-none dark:brightness-0 dark:invert" />
                </span>
              )}
            </Link>
          </div>

          <nav className="flex-1 space-y-4 overflow-y-auto px-3 pb-4 pt-2" aria-label="Menu principal">
            {navigationGroups.map((group, gi) => (
              <div key={group.label || `group-${gi}`} className="space-y-0.5">
                {group.label && expandido && (
                  <p className="px-3 pb-1 text-[11px] font-medium uppercase tracking-[0.06em] text-gray-500">
                    {group.label}
                  </p>
                )}
                {group.items.map(navLink)}
              </div>
            ))}
          </nav>

          <div className="shrink-0 border-t border-border p-3">
            <button
              type="button"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`mb-1 hidden w-full items-center gap-2.5 lg:flex rounded-lg py-1.5 text-[12.5px] text-gray-500 transition-colors hover:bg-accent hover:text-foreground ${sidebarOpen ? 'px-3' : 'justify-center'}`}
              aria-label={sidebarOpen ? 'Recolher menu' : 'Expandir menu'}
            >
              {sidebarOpen ? <PanelLeftClose size={15} /> : <PanelLeftOpen size={15} />}
              {sidebarOpen && 'Recolher menu'}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={`flex w-full items-center gap-2.5 rounded-lg p-2 text-left transition-colors hover:bg-accent ${expandido ? '' : 'justify-center'}`}
                  data-testid="user-menu"
                >
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-primary text-xs font-semibold text-primary-foreground">
                    {getInitials(user?.name)}
                  </span>
                  {expandido && (
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-medium">{user?.name}</span>
                      <span className="block truncate text-xs capitalize text-gray-500">{user?.role?.replace('_', ' ')}</span>
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="w-60">
                <DropdownMenuLabel>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-xs font-normal text-gray-500">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setProfileModalOpen(true)}>
                  <User size={16} />
                  Ver perfil
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs font-medium text-gray-500">Tema</DropdownMenuLabel>
                <DropdownMenuRadioGroup value={preference} onValueChange={setPreference}>
                  <DropdownMenuRadioItem value="system" data-testid="theme-system">Igual ao computador</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="light" data-testid="theme-light">Claro</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="dark" data-testid="theme-dark">Escuro</DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
                {pet.pode && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuCheckboxItem
                      checked={petVisivel}
                      onCheckedChange={pet.mostrar}
                      data-testid="mostrar-pet"
                    >
                      Mostrar meu pet
                    </DropdownMenuCheckboxItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                  <LogOut size={16} />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        <div className={`flex min-h-screen min-w-0 flex-1 flex-col transition-[margin] duration-200 ${sidebarOpen ? 'lg:ml-56' : 'lg:ml-[72px]'}`}>
          {/* celular: faixa cobalto com o menu, emendada na capa da página */}
          <div className="flex h-14 items-center justify-between bg-cover px-4 text-cover-foreground lg:hidden">
            <Link to="/" aria-label="BCMED, ir para o Dashboard">
              <img src="/logo-bcmed.png" alt="BCMED" className="h-8 w-auto brightness-0 invert" />
            </Link>
            <button
              type="button"
              ref={botaoMenuRef}
              onClick={() => setMenuMobile(true)}
              aria-label="Abrir menu"
              aria-controls="menu-lateral"
              aria-expanded={menuMobile}
              className="grid h-9 w-9 place-items-center rounded-lg transition-colors hover:bg-white/15"
            >
              <Menu size={20} />
            </button>
          </div>
          <main className={petVisivel ? 'flex-1 pb-32' : 'flex-1'} key={location.pathname}>
            <Outlet />
          </main>
        </div>

        <UserProfileModal
          open={profileModalOpen}
          onClose={() => setProfileModalOpen(false)}
          user={user}
        />
        {petVisivel && (
          <LimitePet>
            <Suspense fallback={null}>
              <PetWidget
                estado={pet.estado}
                recemNascido={pet.recemNascido}
                onFimDoNascimento={pet.fimDoNascimento}
                onEscolherNome={pet.escolherNome}
                onEscolherPet={pet.escolherPet}
                onEsconder={() => pet.mostrar(false)}
                onVisto={pet.marcarVisto}
                onRecarregar={pet.recarregar}
              />
            </Suspense>
          </LimitePet>
        )}
      </div>
    </TooltipProvider>
  );
};

export default Layout;
